/**
 * Autorização por PIN — spec 04.
 *
 * O desenho resolve um problema concreto de balcão: o vendedor não pode dar
 * 30 % de desconto, mas a gerente também não vai deslogar e logar de novo no
 * meio do atendimento. Então ela digita quatro dígitos NA SESSÃO DELE, a ação
 * sai, e o registro de auditoria guarda quem autorizou.
 *
 * O PIN não é senha e não vira sessão: vale para uma requisição e só. Quem
 * garante isso é o servidor (`porteiro.Autorizar`) — este componente só
 * coleta.
 */
import { useEffect, useRef, useState } from 'react'

import { Botao } from './Botao'
import { Icone } from './Icone'
import { usarEsc, usarFocoPreso } from './foco'

const DIGITOS = 4

/** Rótulo do que está sendo autorizado, por chave de permissão. */
const O_QUE_AUTORIZA: Record<string, string> = {
  'stock.adjust': 'lançar ajuste de estoque',
  'sale.discount_above_limit': 'dar desconto acima do limite',
  'sale.cancel': 'cancelar a venda',
  'cash.movement': 'fazer sangria ou suprimento',
  'return.create': 'registrar a devolução',
  'purchase.approve_high': 'aprovar o pedido de compra',
  'inventory.close': 'fechar o inventário',
}

export function DialogoPIN({
  permissao,
  erro,
  ocupado,
  aoConfirmar,
  aoCancelar,
}: {
  /** Chave devolvida em `campos.requires_pin`. */
  permissao: string
  /** Mensagem da última tentativa recusada, se houve. */
  erro?: string
  ocupado?: boolean
  aoConfirmar: (pin: string) => void
  aoCancelar: () => void
}) {
  const [pin, setPin] = useState('')
  const campo = useRef<HTMLInputElement>(null)
  const painel = useRef<HTMLDivElement>(null)

  // `usarFocoPreso` já traz o foco para o primeiro focável — que aqui é o
  // campo do PIN — além de fechar o ciclo do Tab e devolver o foco ao fechar.
  // Quem autoriza está de pé ao lado do operador: um clique a mais no campo é
  // um clique a mais de fila no caixa.
  usarFocoPreso(painel)

  // O PIN errado limpa o campo. Manter os quatro dígitos na tela faria a
  // pessoa corrigir um dígito por tentativa — e o freio de tentativa conta
  // cada uma delas.
  useEffect(() => {
    if (erro) setPin('')
  }, [erro])

  usarEsc(aoCancelar)

  const acao = O_QUE_AUTORIZA[permissao] ?? 'concluir esta ação'
  const completo = pin.length === DIGITOS

  const confirmar = () => {
    if (completo && !ocupado) aoConfirmar(pin)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(15,23,42,0.45)] px-4">
      <div
        ref={painel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="titulo-pin"
        tabIndex={-1}
        className="w-full max-w-[380px] rounded-card bg-superficie p-6 shadow-[0_20px_50px_rgba(15,23,42,0.25)]"
      >
        <div className="mb-4 flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sistema bg-marca-suave text-marca">
            <Icone nome="lock" tamanho={20} />
          </span>
          <div>
            <h2 id="titulo-pin" className="font-titulo text-[17px] font-bold text-titulo">
              Autorização de gerente
            </h2>
            <p className="mt-1 text-[13px] leading-[1.5] text-texto-suave">
              Seu perfil pode {acao}, mas precisa da liberação de alguém com acesso total.
            </p>
          </div>
        </div>

        <label htmlFor="pin" className="mb-2 block text-[12.5px] font-semibold text-texto">
          PIN de 4 dígitos
        </label>
        <input
          id="pin"
          ref={campo}
          value={pin}
          inputMode="numeric"
          autoComplete="off"
          // `password` esconde o PIN de quem está na fila do caixa olhando a
          // tela por cima do ombro.
          type="password"
          maxLength={DIGITOS}
          onChange={(evento) => setPin(evento.target.value.replace(/\D/g, '').slice(0, DIGITOS))}
          onKeyDown={(evento) => {
            if (evento.key === 'Enter') confirmar()
          }}
          aria-invalid={Boolean(erro)}
          aria-describedby={erro ? 'erro-pin' : undefined}
          className={`tabular w-full rounded-auth border px-4 py-3 text-center text-[24px] font-bold tracking-[0.4em] ${
            erro ? 'border-erro' : 'border-borda'
          }`}
        />

        {erro && (
          <p id="erro-pin" role="alert" className="mt-2 text-[12.5px] font-medium text-erro">
            {erro}
          </p>
        )}

        {/* Os invólucros com `flex-1` existem porque o `Botao` é `shrink-0` —
            ele não deve encolher quando está ao lado de um irmão maior. Aqui a
            divisão em duas metades iguais vem do PAI, não do botão. */}
        <div className="mt-5 flex gap-2.5">
          <div className="flex-1">
            <Botao variante="secundario" porte="medio" larguraTotal onClick={aoCancelar}>
              Cancelar
            </Botao>
          </div>
          <div className="flex-1">
            <Botao
              porte="medio"
              larguraTotal
              disabled={!completo || ocupado}
              onClick={confirmar}
            >
              {ocupado ? 'Autorizando…' : 'Autorizar'}
            </Botao>
          </div>
        </div>

        <p className="mt-3.5 text-center text-[11.5px] leading-[1.5] text-texto-fraco">
          Quem autorizar fica registrado no movimento e na auditoria.
        </p>
      </div>
    </div>
  )
}
